
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ArrowRight, Shield } from 'lucide-react';
import { APPROVED_CLAIMS } from '../config/claimsConfig';

interface LayoutProps {
  children: React.ReactNode;
  user?: { phone: string; name: string } | null;
  onLogout?: () => void;
}

const Logo: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`flex flex-col items-start leading-none ${className}`}>
    <span className="text-3xl md:text-4xl font-black text-black tracking-tight uppercase">CHUI</span>
    <span className="text-[13px] md:text-[15px] font-bold text-[#2d334a] mt-0.5 whitespace-nowrap">Business Credit</span>
    <span className="text-[9px] md:text-[10px] font-bold text-[#b91c1c] mt-0.5 whitespace-nowrap">Member of Ikhaya Group</span>
  </div>
);

const Layout: React.FC<LayoutProps> = ({ children, user, onLogout }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLink = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    setIsMenuOpen(false);
    
    if (location.pathname === '/' || location.pathname === '') {
      e.preventDefault();
      const element = document.getElementById(hash);
      if (element) {
        const offset = 100;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        
        navigate(`/#${hash}`);
      }
    }
  };

  const navLinks = [
    { name: 'Features', hash: 'features' },
    { name: 'How It Works', hash: 'how-it-works' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <nav className="bg-white/95 backdrop-blur-md border-b border-slate-100 py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 md:h-24">
            <Link to="/" className="flex items-center group shrink-0">
              <Logo className="transition-premium group-hover:scale-105" />
            </Link>

            <div className="hidden lg:flex items-center space-x-10">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={`/#${link.hash}`}
                  onClick={(e) => handleLink(e, link.hash)}
                  className="text-[13px] font-bold text-slate-500 hover:text-[#2d334a] transition-all uppercase tracking-wider"
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/faqs" className="text-[13px] font-bold text-slate-500 hover:text-[#2d334a] transition-all uppercase tracking-wider">FAQs</Link>
              <Link to="/pricing-methodology" className="text-[13px] font-bold text-slate-500 hover:text-[#2d334a] transition-all uppercase tracking-wider">Rates</Link>
            </div>

            <div className="hidden lg:flex items-center space-x-4">
              {user ? (
                <Link
                  to="/portal/admin"
                  className="bg-chui-navy text-white px-7 py-3 rounded-2xl font-black text-[13px] flex items-center hover:bg-slate-800 transition-all shadow-xl shadow-slate-200 active:scale-95 uppercase tracking-widest"
                >
                  <Shield size={14} className="mr-2 text-chui-yellow" /> {user.name.split(' ')[0]}'s Workspace
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="bg-slate-100 text-slate-500 px-6 py-3 rounded-xl font-bold text-[11px] flex items-center hover:bg-slate-200 transition-all uppercase tracking-widest"
                >
                  Admin Portal
                </Link>
              )}
              <Link
                to="/apply"
                className="bg-[#2d334a] text-white px-8 py-4 rounded-2xl font-black text-[13px] flex items-center hover:bg-slate-800 transition-all shadow-xl shadow-slate-200 active:scale-95 uppercase tracking-[0.1em]"
              >
                Apply Now <ArrowRight size={14} className="ml-2" />
              </Link>
            </div>

            <div className="lg:hidden">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-slate-600 p-2 hover:bg-slate-50 rounded-lg">
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 w-full bg-white border-b border-slate-100 shadow-2xl p-6 space-y-6 animate-in slide-in-from-top duration-300">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={`/#${link.hash}`}
                onClick={(e) => handleLink(e, link.hash)}
                className="block text-sm font-black text-slate-600 uppercase tracking-widest text-left"
              >
                {link.name}
              </Link>
            ))}
            <Link to="/faqs" onClick={() => setIsMenuOpen(false)} className="block text-sm font-black text-slate-600 uppercase tracking-widest text-left">FAQs</Link>
            <Link to="/pricing-methodology" onClick={() => setIsMenuOpen(false)} className="block text-sm font-black text-slate-600 uppercase tracking-widest text-left">Pricing</Link>
            <Link to="/apply" onClick={() => setIsMenuOpen(false)} className="block text-sm font-black text-slate-600 uppercase tracking-widest text-left">Apply Now</Link>
            <hr className="border-slate-50" />
            <div className="grid grid-cols-1 gap-4">
              <Link to="/login" onClick={() => setIsMenuOpen(false)} className="block bg-slate-100 text-slate-500 text-center py-4 rounded-xl font-black text-sm uppercase tracking-widest">Admin Portal</Link>
            </div>
          </div>
        )}
      </nav>

      <main className="flex-grow">{children}</main>

      <footer className="bg-white text-slate-400 py-24 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-16 mb-20 text-left">
            <div className="lg:col-span-6">
              <Link to="/" className="mb-8 block">
                <Logo />
              </Link>
              <p className="text-slate-500 text-lg max-w-md leading-relaxed mb-10 font-medium">
                Developing Kenya's most transparent character-based credit ecosystem for the micro-entrepreneurs who drive our economy.
              </p>
            </div>
            
            <div className="lg:col-span-3">
              <h5 className="text-[#2d334a] font-black uppercase tracking-[0.2em] text-[11px] mb-10">ECOSYSTEM</h5>
              <ul className="space-y-6 font-black text-[#2d334a] text-[14px] tracking-widest">
                <li><Link to="/pricing-methodology" className="hover:text-slate-600 transition-colors">PRICING</Link></li>
                <li><Link to="/faqs" className="hover:text-slate-600 transition-colors">FAQS</Link></li>
                <li><Link to="/apply" className="hover:text-slate-600 transition-colors">APPLY</Link></li>
                <li><Link to="/login" className="hover:text-slate-600 transition-colors">INTERNAL ACCESS</Link></li>
              </ul>
            </div>

            <div className="lg:col-span-3">
              <h5 className="text-[#2d334a] font-black uppercase tracking-[0.2em] text-[11px] mb-10">LEGAL & COMPLIANCE</h5>
              <ul className="space-y-6 font-black text-[#2d334a] text-[14px] tracking-widest">
                <li><Link to="/terms" className="hover:text-slate-600 transition-colors uppercase">TERMS</Link></li>
                <li><Link to="/privacy" className="hover:text-slate-600 transition-colors uppercase">PRIVACY</Link></li>
                <li><Link to="/complaints" className="hover:text-slate-600 transition-colors uppercase">DISPUTES</Link></li>
                <li><Link to="/collections" className="hover:text-slate-600 transition-colors uppercase">FAIR DEBT</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-12 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex flex-col items-center md:items-start text-center md:text-left">
            </div>
            <a href={`mailto:${APPROVED_CLAIMS.SUPPORT_EMAIL}`} className="font-black text-slate-400 text-[10px] uppercase tracking-[0.3em] hover:text-chui-navy transition-colors">
              {APPROVED_CLAIMS.SUPPORT_EMAIL}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
